import type { FC, SVGProps } from 'react';
import clsx from 'clsx';
import {
  composeRenderProps,
  MenuItem as AriaMenuItem,
  type MenuItemProps as AriaMenuItemProps,
  RadioGroup,
  SubmenuTrigger,
} from 'react-aria-components';
import { NotAffectingLineHeightObject } from '../../not-affecting-line-height-object';
import { Checkbox } from '../../checkbox';
import { Text } from '../../typography';
import { Grid } from '../../layout';
import { PopoverTooltip, type PopoverTooltipProps } from '../../tooltip';
import { Radio } from '../../radio';
import styles from './MenuItem.module.css';
import commonStyles from '../Dropdowns.module.css';

type Props = Required<Pick<AriaMenuItemProps, 'id'>> &
  Pick<AriaMenuItemProps, 'onAction' | 'href' | 'isDisabled' | 'children' | 'textValue'> & {
    icon?: FC<SVGProps<SVGSVGElement>>;
  } & (
    | {
        isDisabled?: boolean;
        isUnavailable?: undefined;
        tooltipText?: undefined;
        tooltipPlacement?: undefined;
        tooltipOffset?: undefined;
      }
    | {
        isDisabled?: undefined;
        isUnavailable?: boolean;
        tooltipText?: PopoverTooltipProps['text'];
        tooltipPlacement?: PopoverTooltipProps['placement'];
        tooltipOffset?: PopoverTooltipProps['offset'];
      }
  );

export function MenuItem({
  id,
  children: childrenFromProps,
  textValue: textValueFromProps,
  isDisabled,
  onAction,
  href,
  icon: Icon,
  isUnavailable,
  tooltipText,
  tooltipPlacement,
  tooltipOffset,
}: Props) {
  const textValue = textValueFromProps || (typeof childrenFromProps === 'string' ? childrenFromProps : undefined);

  const menuItem = (
    <AriaMenuItem
      id={id}
      onAction={onAction}
      href={href}
      isDisabled={isDisabled}
      textValue={textValue}
      className={clsx(commonStyles.item, styles.item)}
      data-unavailable={isUnavailable ? 'true' : undefined}
    >
      {composeRenderProps(childrenFromProps, (children, { isSelected, selectionMode }) => {
        return (
          <>
            {selectionMode === 'multiple' && (
              <span className={styles.checkbox}>
                <Checkbox isSelected={isSelected} isDisabled={isDisabled} />
              </span>
            )}
            {selectionMode === 'single' && (
              <span className={styles.checkbox}>
                <RadioGroup value={isSelected ? 'selected' : undefined}>
                  <Radio value="selected" isDisabled={isDisabled} />
                </RadioGroup>
              </span>
            )}
            {Icon ? (
              <span className={styles.icon}>
                <NotAffectingLineHeightObject>
                  <Icon className={styles.svg} />
                </NotAffectingLineHeightObject>
              </span>
            ) : null}
            <Grid area="label">{typeof children === 'string' ? <Text slot="label">{children}</Text> : children}</Grid>
          </>
        );
      })}
    </AriaMenuItem>
  );

  // https://github.com/adobe/react-spectrum/discussions/8437
  if (isUnavailable && tooltipText) {
    return (
      <SubmenuTrigger>
        {menuItem}
        <PopoverTooltip
          aria-label="Unavailable menu item description"
          text={tooltipText}
          placement={tooltipPlacement}
          offset={tooltipOffset}
        />
      </SubmenuTrigger>
    );
  }

  return menuItem;
}
